import { BackButton } from "@/components/custom/BackButton"
import { PageTitle } from "@/components/custom/PageTitle"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { UserRole, userRole } from "@/constants/users/userRole"
import { useSelector, useDispatch } from 'react-redux'
import { AppDispatch, RootState } from "@/redux"
import { inviteUserToOrganization } from "@/services/userService"
import { useNavigate } from "react-router-dom"
import { Form } from "@/components/ui/form"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { getFormProps } from "@/helpers/formHelpers"
import { SelectInputField } from "@/components/custom/InputField/SelectInputField"
import { useEffect } from "react"

const formSchema = z.object({
  email: z.string().email(),
  role: z.string().min(3, {
    message: 'Please Select User Role.'
  })
})

export const CreateUser = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      role: ""
    },
  })
  const formProps = getFormProps(form, formSchema)

  const onFormSubmit = async (
    values: z.infer<typeof formSchema>
  ) => {
    if (!token) {
      return
    }
    const response = await dispatch(inviteUserToOrganization(token, values))
    if (response.data) {
      form.reset({ email: "", role: "" })
      navigate('/users')
    }
  }

  useEffect(() => {
    if (userData?.role === UserRole.USER) {
      navigate('/users')
    }
  }, [userData, navigate])

  return (
    <div className="protectedPageLayout">
      <BackButton buttonLink="/users" buttonText="Back to Users" />
      <PageTitle title="Add New User" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className="space-y-8 flex flex-col items-start"
        >
          <TextInputField
            name="email"
            label="Email Address"
            error={!!form.formState.errors.email}
            type="email"
            placeholder="john@example.com"
            required={true}
            {...formProps}
          />
          <SelectInputField
            name="role"
            label="Role"
            selectOptions={userRole}
            required={true}
            {...formProps}
          />
          <Button type="submit" className="mb-2 font-semibold">
            Add New User
          </Button>
        </form>
      </Form>
    </div>
  )
}
